import { useMemo, useState } from 'react';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { ConfirmationDialogV2, ErpDataTableV2, ErpInputTableV2 } from '@/components/custom/erp';
import { getDataTableColumns, getInputTableColumns } from './cols-definition';
import { ActionBar, FormDialog, SearchDialog } from './components';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

const PREVIEW_ACTION_ORDER = ['export', 'pin'] as const;

export default function ButToanDieuChinhCongNo() {
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const {
    data: butToanDieuChinhGiamCongNos,
    isLoading,
    totalItems,
    currentPage,
    handlePageChange,
    detail,
    fetchDetail,
    addItem,
    updateItem,
    deleteItem,
    refreshData
  } = useCRUD({
    endpoint: QUERY_KEYS.BUT_TOAN_DIEU_CHINH_GIAM_CONG_NO
  });

  const {
    showForm,
    showDelete,
    formMode,
    isCopyMode,
    selectedObj,
    selectedRowIndex,
    handleRowClick,
    clearSelection,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick
  } = useForms(fetchDetail);

  const handleSearch = () => {
    setShowSearchDialog(true);
  };

  const handleSearchClose = () => {
    setShowSearchDialog(false);
  };

  const handleSearchSubmit = (filters: BanHangPayload) => {
    console.log('Search filters:', filters);
    // TODO: Implement search functionality here
  };

  const handleSubmit = async (data: BanHangPayload) => {
    try {
      if (formMode === 'add') {
        await addItem(data);
      } else if (formMode === 'edit' && selectedObj) {
        await updateItem(selectedObj.uuid, data);
      }
      handleCloseForm();
      clearSelection();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const mainColumns = useMemo(() => getDataTableColumns(handleViewClick), [handleViewClick]);
  const previewColumns = useMemo(() => getInputTableColumns(selectedObj?.ma_ngv), [selectedObj?.ma_ngv]);
  const previewActions = useMemo(
    () => ({
      export: () => console.log('Export clicked'),
      pin: () => console.log('Pin clicked')
    }),
    []
  );

  const tables = [
    {
      name: 'Tất cả',
      rows: butToanDieuChinhGiamCongNos,
      columns: mainColumns
    },
    {
      name: 'Chưa ghi sổ',
      rows: butToanDieuChinhGiamCongNos.filter(row => row.status === '0'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#EAD1DC]' />
    },
    {
      name: 'Chờ duyệt',
      rows: butToanDieuChinhGiamCongNos.filter(row => row.status === '3'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-red-500' />
    },
    {
      name: 'Đã ghi sổ',
      rows: butToanDieuChinhGiamCongNos.filter(row => row.status === '5'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#3D85C6]' />
    },
    {
      name: 'Khác',
      rows: butToanDieuChinhGiamCongNos.filter(row => row.status !== '0' && row.status !== '3' && row.status !== '5'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-black' />
    }
  ];

  return (
    <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col overflow-hidden'>
      {showSearchDialog && (
        <SearchDialog open={showSearchDialog} onClose={handleSearchClose} onSearch={handleSearchSubmit} />
      )}

      {showForm && (
        <FormDialog
          formMode={formMode}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onClose={handleCloseForm}
          onSubmit={handleSubmit}
        />
      )}

      {showDelete && (
        <ConfirmationDialogV2
          open={showDelete}
          onClose={handleCloseDelete}
          onConfirm={() => {
            if (selectedObj?.uuid) deleteItem(selectedObj.uuid);
            clearSelection();
            handleCloseDelete();
          }}
        />
      )}

      {!showForm && (
        <>
          <ActionBar
            onAdd={handleAddClick}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onCopy={handleCopyClick}
            onSearch={handleSearch}
            onRefresh={async () => {
              await refreshData();
              fetchDetail(selectedObj?.uuid);
            }}
            isViewDisabled={!selectedObj}
          />

          <ResizablePanelGroup direction='vertical' className='flex-1 overflow-hidden'>
            <ResizablePanel defaultSize={50} minSize={20}>
              <div className='h-full w-full overflow-hidden' data-perf-id='btdc-giam-cong-no-master-table'>
                <ErpDataTableV2
                  tables={tables}
                  onRowClick={handleRowClick}
                  selectedRowId={selectedRowIndex || undefined}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  loading={isLoading}
                />
              </div>
            </ResizablePanel>

            <ResizableHandle withHandle />

            <ResizablePanel defaultSize={50} minSize={20}>
              <div className='h-full overflow-hidden' data-perf-id='btdc-giam-cong-no-bottom-detail-table'>
                <ErpInputTableV2
                  rows={selectedObj && !isLoading ? detail?.chi_tiet_data || [] : []}
                  columns={previewColumns}
                  showIndexColumn
                  actions={previewActions}
                  actionOrder={PREVIEW_ACTION_ORDER as BanHangRecord}
                  actionsDisabled={formMode === 'view' || !selectedObj}
                  height='100%'
                  loading={isLoading && !selectedObj}
                />
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </>
      )}
    </div>
  );
}
